import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent,
} from './ui/dialog';
import { Input } from './ui/input';
import {
  Search, Building2, Server, Package, BarChart3, Users, Settings, Plus,
  FileText, Zap, Brain, Shield, ArrowRight, Clock, Share2, Keyboard, Focus, Download, RefreshCw, Sparkles,
} from 'lucide-react';
import { useRecentViewsApi } from './RecentViewsDropdown';
import { useFocusMode } from './common/FocusMode';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const ACTIONS = [
  { id: 'go-home', label: 'Go to Home Dashboard', icon: Building2, tab: 'home' },
  { id: 'go-stackmap', label: 'Open Stack Map', icon: Server, tab: 'visualizer' },
  { id: 'go-reports', label: 'Open Reports', icon: BarChart3, tab: 'reports' },
  { id: 'go-database', label: 'Open Tech Database', icon: Package, tab: 'database' },
  { id: 'go-ai', label: 'Open AI Assistant', icon: Brain, tab: 'ai' },
  { id: 'go-admin', label: 'Open Admin', icon: Settings, tab: 'admin' },
  { id: 'go-wizard', label: 'Open Property Wizard', icon: Zap, tab: 'wizard' },
  { id: 'go-documents', label: 'Open Document Vault', icon: FileText, tab: 'documents' },
  { id: 'go-users', label: 'Manage Users', icon: Users, tab: 'user-mgmt' },
  { id: 'go-templates', label: 'Stack Templates', icon: Zap, tab: 'templates' },
  { id: 'go-benchmarks', label: 'Competitive Benchmarks', icon: BarChart3, tab: 'benchmarks' },
  { id: 'go-security', label: 'Security Dashboard', icon: Shield, tab: 'security' },
  { id: 'create-property', label: 'Create New Property', icon: Plus, tab: 'wizard' },
  { id: 'add-user', label: 'Add New User', icon: Plus, tab: 'user-mgmt' },
  { id: 'share-view', label: 'Share this View (Copy Link)', icon: Share2, action: 'share' },
  // D7. Cmd+K actions — not just navigation
  { id: 'toggle-focus', label: 'Toggle Focus Mode (Shift+F)', icon: Focus, action: 'toggle-focus' },
  { id: 'show-shortcuts', label: 'Show Keyboard Shortcuts (?)', icon: Keyboard, action: 'show-shortcuts' },
  { id: 'reload-stack', label: 'Refresh Current Stack Map', icon: RefreshCw, action: 'reload-stack' },
  { id: 'export-coverage', label: 'Export Coverage as CSV', icon: Download, action: 'export-coverage' },
  { id: 'open-audit', label: 'Run 30-Day Tech Stack Audit', icon: Sparkles, tab: 'ai' },
];

export default function CommandPalette({ open, onOpenChange, token, onNavigate, properties, stackData, selectedProperty, onJumpToNode, onRefreshStack }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { views: recentViews } = useRecentViewsApi(token);
  const { toggle: toggleFocus } = useFocusMode();

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      // Empty state: show recent views first, then top actions
      const recentAsItems = (recentViews || []).slice(0, 5).map(v => ({
        id: `recent-${v.key}`,
        label: v.node_name,
        subtitle: v.property_name,
        icon: Clock,
        type: 'recent',
        propertyId: v.property_id,
        nodeId: v.node_id,
      }));
      setResults([...recentAsItems, ...ACTIONS.slice(0, 6).map(a => ({ ...a, type: 'action' }))]);
      setSelectedIdx(0);
      return;
    }
    const q = query.toLowerCase();

    // Search actions
    const actionResults = ACTIONS
      .filter(a => a.label.toLowerCase().includes(q))
      .map(a => ({ ...a, type: 'action' }));

    // Search properties
    const propResults = (properties || [])
      .filter(p => p.name.toLowerCase().includes(q))
      .slice(0, 5)
      .map(p => ({
        id: `prop-${p.id}`, label: p.name, icon: Building2, type: 'property', propertyId: p.id,
        subtitle: p.type,
      }));

    // Search platforms/nodes in the current stack (only when viewing a property)
    const nodeResults = (stackData?.nodes || [])
      .filter(n => {
        const name = (n.name || '').toLowerCase();
        const short = (n.short || '').toLowerCase();
        const vendor = (n.vendor_name || n.product_name || '').toLowerCase();
        return name.includes(q) || short.includes(q) || vendor.includes(q);
      })
      .slice(0, 8)
      .map(n => ({
        id: `node-${n.id}`, label: n.name, icon: Server, type: 'node', nodeId: n.id,
        subtitle: n.vendor_name || n.product_name || n.short || '',
        propertyId: selectedProperty || 'overall',
      }));

    setResults([
      ...nodeResults,
      ...propResults,
      ...actionResults.slice(0, 5),
    ]);
    setSelectedIdx(0);
  }, [query, properties, stackData, selectedProperty, recentViews]);

  const execute = (item) => {
    onOpenChange(false);
    if (item.action === 'share') {
      try {
        navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard — anyone with access will land on this exact view');
      } catch {
        toast.error('Could not copy link — copy it manually from the address bar');
      }
      return;
    }
    if (item.action === 'toggle-focus') {
      toggleFocus();
      toast.success('Focus mode toggled');
      return;
    }
    if (item.action === 'show-shortcuts') {
      window.dispatchEvent(new CustomEvent('lodgiflow:show-shortcuts'));
      return;
    }
    if (item.action === 'reload-stack') {
      if (onRefreshStack) { onRefreshStack(); toast.success('Refreshing stack map…'); }
      else toast.info('Open the Stack Map to refresh it');
      return;
    }
    if (item.action === 'export-coverage') {
      window.dispatchEvent(new CustomEvent('lodgiflow:export-coverage'));
      toast.success('Preparing coverage CSV export…');
      return;
    }
    if (item.type === 'recent' || item.type === 'node') {
      if (onJumpToNode) onJumpToNode({ property_id: item.propertyId, node_id: item.nodeId });
      return;
    }
    if (item.tab) {
      onNavigate(item.tab, item.propertyId);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIdx]) {
      execute(results[selectedIdx]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" onKeyDown={handleKeyDown}>
        <div className="flex items-center gap-3 px-4 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search or type a command..."
            className="flex-1 h-12 bg-transparent outline-none text-sm"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border bg-muted text-muted-foreground font-mono">ESC</kbd>
        </div>
        <div className="max-h-[300px] overflow-y-auto py-1">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No results for &ldquo;{query}&rdquo;</div>
          ) : (
            results.map((item, idx) => {
              const Icon = item.icon || ArrowRight;
              return (
                <button
                  key={item.id}
                  onClick={() => execute(item)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${idx === selectedIdx ? 'bg-accent' : 'hover:bg-muted/60'}`}
                  data-testid={`command-result-${item.id}`}
                >
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.subtitle && <span className="text-xs text-muted-foreground ml-2">{item.subtitle}</span>}
                  </div>
                  {item.type === 'action' && <span className="text-[10px] text-muted-foreground">Action</span>}
                  {item.type === 'property' && <span className="text-[10px] text-muted-foreground">Property</span>}
                  {item.type === 'node' && <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">Platform</span>}
                  {item.type === 'recent' && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">Recent</span>}
                </button>
              );
            })
          )}
        </div>
        <div className="border-t px-4 py-2 flex items-center gap-4 text-[10px] text-muted-foreground">
          <span><kbd className="px-1 py-0.5 rounded border bg-muted font-mono">↑↓</kbd> Navigate</span>
          <span><kbd className="px-1 py-0.5 rounded border bg-muted font-mono">↵</kbd> Select</span>
          <span><kbd className="px-1 py-0.5 rounded border bg-muted font-mono">esc</kbd> Close</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
